import React, { useEffect, useState } from 'react';
import { Col, Form, Input, Modal, Row, Select, Button, notification } from 'antd';

const { confirm } = Modal;
const { Option, OptGroup } = Select;

const DiscountModal = ({ form, offVisible, visibility, selectedDiscount, onSubmit, resetSelectedDiscount, onDelete, hostData }) => {
  const [discountType, setDiscountType] = useState('');
  const [assignedHosts, setAssignedHosts] = useState([]);
  const [unassignedHosts, setUnassignedHosts] = useState([]);
  const [assignedHostsFromOtherDiscount, setAssignedHostsFromOtherDiscount] = useState([]);
  const [allUnassignedHosts, setAllUnassignedHosts] = useState([]);
  const [allHostsAssigned, setAllHostsAssigned] = useState(false);
  const [allHostsUnassigned, setAllHostsUnassigned] = useState(false);
  const [loadingData, setLoadingData] = useState(false);

  useEffect(() => {
    if (selectedDiscount) {
      form.setFieldsValue({
        name: selectedDiscount.name,
        type: selectedDiscount.type,
        amount: selectedDiscount.amount,
        remark: selectedDiscount.remark,
        host: selectedDiscount.linkedHostDetails.map(host => host._id) // Ensure host data is pre-filled if available
      });
      setDiscountType(selectedDiscount.type);
    } else {
      form.resetFields();
    }

    if (hostData) {
      initializeHostData(hostData);
    }
  }, [selectedDiscount, hostData]);

  const initializeHostData = hosts => {
    setLoadingData(true);
    try {
      const selectedHosts = form.getFieldValue('host') || [];
      let hostFromOtherDiscount;

      // Filter out hosts assigned to other discount
      if (selectedDiscount) {
        // Filter out hosts assigned to other pricing models
        hostFromOtherDiscount = hosts.filter(host => host.discount && host.discount !== selectedDiscount._id);
      } else {
        // Filter out hosts assigned to any pricing model
        hostFromOtherDiscount = hosts.filter(host => host.discount);
      }

      setAssignedHostsFromOtherDiscount(hostFromOtherDiscount);

      // Calculate all unassigned hosts
      const allUnassignedHosts = hosts.filter(host => !hostFromOtherDiscount.some(otherHost => otherHost._id === host._id));
      setAllUnassignedHosts(allUnassignedHosts);

      setAssignedHosts(allUnassignedHosts.filter(host => selectedHosts.includes(host._id)));
      setUnassignedHosts(allUnassignedHosts.filter(host => !selectedHosts.includes(host._id)));

      setAllHostsAssigned(allUnassignedHosts.length === 0);
      setAllHostsUnassigned(allUnassignedHosts.length === 0);
    } catch (error) {
      console.error('Error initializing host data:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to initialize host data'
      });
    } finally {
      setLoadingData(false);
    }
  };

  const handleOk = async e => {
    form.validateFields((err, values) => {
      if (!err) {
        onSubmit(values, !selectedDiscount ? 'new' : selectedDiscount._id);
        form.resetFields();
        offVisible();
        resetSelectedDiscount();
      }
    });
  };

  const handleCancel = e => {
    offVisible();
    resetSelectedDiscount();
  };

  const handleConfirmDelete = () => {
    confirm({
      title: 'Are you sure you want to delete this discount?',
      content: (
        <div>
          <p>All the hosts using this discount will have their discount removed. This action cannot be undone.</p>
          {selectedDiscount.linkedHostDetails.length > 0 && (
            <>
              <div>Host(s) that are using the discount : </div>
              <ul>
                {selectedDiscount.linkedHostDetails.map(host => (
                  <li key={host._id}>{host.name}</li>
                ))}
              </ul>
            </>
          )}
        </div>
      ),
      onOk() {
        handleDelete();
        resetSelectedDiscount();
      },
      onCancel() {
        console.log('Cancel');
      }
    });
  };

  const handleDelete = () => {
    offVisible();
    onDelete(selectedDiscount._id);
  };

  const { getFieldDecorator } = form;

  return (
    <Modal
      title={!selectedDiscount ? 'New Discount' : selectedDiscount.name}
      visible={visibility}
      onCancel={handleCancel}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <Button key="submit" type="primary" onClick={handleOk}>
              {!selectedDiscount ? 'Create' : 'Update'}
            </Button>
          </div>
          {selectedDiscount && (
            <Button key="delete" type="danger" onClick={handleConfirmDelete}>
              Delete
            </Button>
          )}
        </div>
      }
    >
      <Form autoComplete="off">
        <Form.Item label="Discount Name">
          {getFieldDecorator('name', {
            rules: [{ required: true, message: 'Please input Discount Name' }]
          })(<Input placeholder="Discount Name" />)}
        </Form.Item>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Discount Type">
              {getFieldDecorator('type', {
                rules: [{ required: true, message: 'Please select Discount Type' }]
              })(
                <Select placeholder="Select Type" onChange={value => setDiscountType(value)}>
                  <Option value="percentage">Percentage</Option>
                  <Option value="amount">Fixed Amount</Option>
                </Select>
              )}
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item label={discountType === 'percentage' ? 'Percentage' : 'Amount'}>
              {getFieldDecorator('amount', {
                rules: [
                  { required: true, message: `${`Please input Discount`} ${discountType === 'percentage' ? 'Percentage' : 'Amount'}` },
                  {
                    pattern: /^[0-9]+(\.[0-9]{1,2})?$/,
                    message: 'Please enter valid numbers and 2 decimal places only'
                  }
                ]
              })(
                <Input
                  placeholder={`${`Discount`} ${discountType === 'percentage' ? 'Percentage' : 'Amount'}`}
                  addonAfter={discountType === 'percentage' ? '%' : ''}
                />
              )}
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Remark">
          {getFieldDecorator('remark', {
            rules: []
          })(<Input.TextArea placeholder="Enter any remarks" rows={3} />)}
        </Form.Item>
        <Form.Item label="Host">
          {getFieldDecorator('host', {
            onChange: value => {
              const newAssignedHosts = allUnassignedHosts.filter(host => value.includes(host._id));
              const newUnassignedHosts = allUnassignedHosts.filter(host => !value.includes(host._id));
              setAssignedHosts(newAssignedHosts);
              setUnassignedHosts(newUnassignedHosts);
              setAllHostsAssigned(newUnassignedHosts.length === 0);
              setAllHostsUnassigned(newAssignedHosts.length === 0);
            }
          })(
            <Select
              mode="multiple"
              placeholder="Select Host"
              disabled={loadingData}
              maxTagCount={100}
              filterOption={(input, option) => {
                const regex = new RegExp(input, 'i');
                return regex.test(option.props.children);
              }}
            >
              <OptGroup label="Unassigned Hosts">
                {unassignedHosts.map(host => (
                  <Option key={host._id} value={host._id}>
                    {host.name}
                  </Option>
                ))}
              </OptGroup>
              <OptGroup label="Assigned Hosts">
                {assignedHosts.map(host => (
                  <Option key={host._id} value={host._id}>
                    {host.name}
                  </Option>
                ))}
              </OptGroup>
              <OptGroup label="Assigned to Other Discount">
                {assignedHostsFromOtherDiscount.map(host => (
                  <Option key={host._id} value={host._id} disabled>
                    {host.name}
                  </Option>
                ))}
              </OptGroup>
            </Select>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default Form.create()(DiscountModal);
